import {
  AddIcon,
  CheckIcon,
  EmptyState,
  Grid,
  GridColumn,
  GridRow,
  Modal,
  SendIcon,
  SpinnerIcon,
} from '@bp/ui-components';
import { BpCalendar } from '../../../components/BpCalendar/BpCalendar';
import { MeetingsLinks } from '../../../components/MeetingsLinks/MeetingsLinks';
import { FC, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { WorkMaterialsList } from '../../../components/WorkMaterialsList/WorkMaterialsList';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import { BpCard } from '../../../components/BpCard/BpCard';
import { AnnouncementsList } from '../../../components/AnnouncementsList/AnnouncementsList';
import { useGetMatrixRoomByName } from '../../../hooks/matrix/useGetMatrixRoomByName';
import { NotificationReason, RoomNames } from '../../../utils/matrixClient';
import { AnnouncementForm, AnnouncementFormType } from '../../../components/AnnouncementForm/AnnouncementForm';
import { useGetAnnouncements } from '../../../hooks/matrix/useGetAnnouncements';
import { NotificationCountType } from 'matrix-js-sdk';
import { SendMatrixMessageForm } from '../../../components/SendMatrixMessageForm/SendMatrixMessageForm';
import { uniqueByUuid } from '../../../utils/uniquByUuid';
import { hasBlockingEvents } from '../../../utils/hasBlockingEvents';
import { backendApi } from '../../../utils/backendApi';
import { graphql } from 'client/gql';
import { useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import dayjs from 'dayjs';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { UserStatus } from 'client/gql/graphql';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { showErrorToast } from '../../../utils/showErrorToast';

const GroupOverviewSubpagePageQueryDocument = graphql(`
  query GroupOverviewSubpagePageData($groupUuid: ID!, $profile: ID!, $organizationUuid: ID!, $formattedDate: String!) {
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      name
      organization {
        uuid
        __typename
      }
      teachingUnits {
        uuid
        __typename
        workMaterials {
          uuid
          __typename
          ...WorkMaterialsListFields
        }
      }
      viewers {
        uuid
        __typename
        userStatus
      }
    }
    ...PermissionCheckerFields
    calendarEvents(where: { holderConnection: { Group: { node: { uuid: $groupUuid } } } }) {
      uuid
      __typename
      color
      start
      virtualLocations {
        uuid
        __typename
      }
    }
    birthdayEvents(groupUuid: $groupUuid) {
      uuid
      __typename
      color
      start
      virtualLocations {
        uuid
        __typename
      }
    }
    ...MeetingsLinksFields
  }
`);

export const GroupOverviewSubpage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganization().uuid;
  const { groupUuid } = useParams<{ groupUuid: string }>();
  const [announcementModal, setAnnouncementModal] = useState(false);
  const [messageModal, setMessageModal] = useState(false);
  const { markAsRead, client } = useMatrixClient();
  const { getRoom } = useGetMatrixRoomByName();

  const [{ data }] = useQuery({
    query: GroupOverviewSubpagePageQueryDocument,
    variables: {
      groupUuid: groupUuid!,
      profile: profileUuid,
      organizationUuid,
      formattedDate: dayjs().format(CONNECT_DATE_TEMPLATE),
    },
    pause: groupUuid === undefined,
  });
  const perms = usePermissionChecker(data);

  const currentGroup = data?.groups?.[0];
  const workMaterial = currentGroup?.teachingUnits[0]?.workMaterials;
  const viewers = currentGroup?.viewers ?? [];

  const events = [...(data?.calendarEvents ?? []), ...(data?.birthdayEvents ?? [])]
    .sort((a, b) => {
      const startA = new Date(a.start).valueOf();
      const startB = new Date(b.start).valueOf();
      return startA - startB;
    })
    .map((e) => ({
      uuid: e.uuid,
      start: dayjs(e.start),
      color: e.color,
      type: e.__typename,
      virtualLocations: e.virtualLocations,
    }));

  const room = getRoom(`${currentGroup?.organization.uuid}_${groupUuid}`, RoomNames.Group);
  const hasUnread = room ? room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0 : false;
  const [loading, setLoading] = useState(false);
  const announcements = useGetAnnouncements(room);

  const receiverProfiles = uniqueByUuid(
    viewers.filter((profile) => {
      return (
        profile.uuid !== pimAuthClaims.getProfile().uuid &&
        (profile.userStatus === UserStatus.Full || profile.userStatus === UserStatus.ImpersonateOnly)
      );
    }),
  );

  if (!groupUuid) {
    return (
      <EmptyState
        fitParent
        forcedHeight='300px'
        size='small'
        subtitle={t('groups.noGroups')}
        iconColor='var(--color-primary-light)'
        icon={<SendIcon />}
      />
    );
  }
  const groupSubject = { uuid: groupUuid ?? '', organization: { uuid: currentGroup?.organization.uuid ?? '' } };

  const handleAnnouncementFormSubmit = async (values: AnnouncementFormType) => {
    if (values) {
      try {
        setLoading(true);
        const roomId = room?.summary?.roomId;

        if (!roomId) {
          throw new Error('Could not find room');
        }

        if (!client) {
          throw new Error('Could not get Matrix Client');
        }

        if (!currentGroup?.name) {
          throw new Error('Could not get groupname');
        }

        const resp = await backendApi.notify(
          {
            type: NotificationReason.GroupAnnouncement,
            content: values.message,
            title: values.title,
            groupUuid,
          },
          { roomId, groupName: currentGroup?.name, client },
        );

        if (!resp.ok) throw new Error(resp.statusText);

        setAnnouncementModal(false);
      } catch (error) {
        showErrorToast({ name: '', graphQLErrors: [], message: error as string });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <BpSubpage>
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <GridColumn width={8}>
            <GridRow mobileGap='var(--grid-column-gap)' direction='column' spacingTop='none'>
              <BpCard
                canScroll
                fixedHeight='374px'
                noPadding
                header={{
                  headline: t('workmaterials.title'),
                  actions: perms?.canCreateBpGroupMaterial({
                    uuid: groupUuid,
                    organization: { uuid: currentGroup?.organization.uuid ?? '' },
                  })
                    ? [
                        {
                          text: t('workmaterials.addMaterial'),
                          icon: <AddIcon />,
                          callback: () => navigate(`/groups/${groupUuid}/workmaterials/add`),
                        },
                      ]
                    : undefined,
                }}
              >
                <WorkMaterialsList
                  workMaterial={workMaterial}
                  onNavigate={(uuid) => navigate(`/groups/${groupUuid}/workmaterials/${uuid}`)}
                  isGroupEditor={
                    !!perms?.isGroupEditor({
                      uuid: groupUuid,
                      organization: { uuid: currentGroup?.organization.uuid ?? '' },
                    })
                  }
                />
              </BpCard>
            </GridRow>
            <GridRow>
              <BpCard
                canScroll
                fixedHeight='374px'
                noPadding
                header={{
                  headline: t('announcements.title'),
                  actions: perms?.canCreateGroupAnnouncement(groupSubject)
                    ? hasUnread
                      ? [
                          {
                            text: t('common.mark'),
                            hierarchy: 'ghost',
                            icon: <CheckIcon />,
                            callback: () => {
                              markAsRead(room);
                            },
                          },
                          {
                            text: t('announcements.new'),
                            icon: <AddIcon />,
                            callback: () => {
                              setAnnouncementModal(true);
                            },
                          },
                        ]
                      : [
                          {
                            text: t('announcements.new'),
                            icon: <AddIcon />,
                            callback: () => {
                              setAnnouncementModal(true);
                            },
                          },
                        ]
                    : hasUnread
                      ? [
                          {
                            text: t('common.mark'),
                            hierarchy: 'ghost',
                            icon: <CheckIcon />,
                            callback: () => {
                              markAsRead(room);
                            },
                          },
                        ]
                      : undefined,
                }}
              >
                <AnnouncementsList
                  announcements={announcements}
                  hasPermissionToDelete={perms?.canCreateGroupAnnouncement(groupSubject) ?? false}
                />
              </BpCard>
            </GridRow>
          </GridColumn>
          <GridColumn width={4}>
            <GridRow spacingTop='none'>
              <BpCard className='pb-6 pr-6 pl-6' noPadding showBorder hideBackground>
                <BpCalendar
                  events={events}
                  showAddButton={!!groupUuid}
                  onAdd={() => navigate(`/groups/${groupUuid}/appointments/create`)}
                  eventClick={() => navigate(`/groups/${groupUuid}/appointments`)}
                />
              </BpCard>
            </GridRow>
            <GridRow>
              <BpCard hideBackground noPadding showOverflow>
                <MeetingsLinks
                  groupUuid={groupUuid}
                  hasBlockingEvents={hasBlockingEvents(events)}
                  query={data}
                  onLessonClick={() => navigate('/meetings/planned')}
                  onMessageClick={() => setMessageModal(true)}
                />
              </BpCard>
            </GridRow>
          </GridColumn>
        </GridRow>
      </Grid>

      {room && (
        <Modal
          isOpen={announcementModal}
          onRequestClose={() => setAnnouncementModal(false)}
          isFormModal
          hideFooter
          title={t('announcements.new')}
          width='s'
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <AnnouncementForm
              onSubmit={handleAnnouncementFormSubmit}
              loading={loading}
              closeModal={() => setAnnouncementModal(false)}
            />
          </Suspense>
        </Modal>
      )}

      {receiverProfiles && (
        <Modal
          isOpen={messageModal}
          onRequestClose={() => setMessageModal(false)}
          isFormModal
          hideFooter
          width={'s'}
          title={t('common.sendToAllGroupViewers')}
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <SendMatrixMessageForm onClose={() => setMessageModal(false)} groupUuid={groupUuid} />
          </Suspense>
        </Modal>
      )}
    </BpSubpage>
  );
};
